// Redesign 2026 – vlastní rozbalovací seznam místo systémového tam, kde prohlížeč neumí
// `appearance: base-select`: Safari na Macu i na iPhonu (na iOS jedou na WebKitu všechny prohlížeče,
// i Chrome) a Firefox. Chromium 135+ má seznam nastylovaný jen v CSS (components/_fluentform.scss)
// a tenhle kód se tam nespouští. Zatím jen select emisí ve formuláři 7 (volá ho order.js).
//
// Skutečný <select> zůstává ve formuláři – Fluent Forms ho odesílá a validuje, order.js ho poslouchá –,
// jen se schová do ořezového obalu. Místo něj tlačítko role="combobox" se seznamem role="listbox" podle
// vzoru WAI-ARIA „select-only combobox“: šipky, Home/End, Enter/mezerník, Esc, Tab, skok na první znak.
// Fokus zůstává na tlačítku, aktivní volbu nese aria-activedescendant. Změny selectu zvenku (předvolba
// emise z karty, reset formuláře) se propíšou přes `change` / `reset`.
// Vzhled: .rd-select v components/_fluentform.scss.

let counter = 0;

/** Chromium 135+ umí select nastylovat v CSS – vlastní seznam tam netřeba. */
export function supportsStyledSelect() {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('appearance', 'base-select');
}

function el(tag, className, attrs = {}) {
  const node = document.createElement(tag);
  node.className = className;
  Object.entries(attrs).forEach(([name, value]) => node.setAttribute(name, value));
  return node;
}

export default function enhanceSelect(select) {
  if (!select || select.multiple || select.closest('.rd-select')) {
    return;
  }
  counter += 1;
  const id = `rd-select-${counter}`;

  // popisek pole: FF dává selectu aria-labelledby, jinak <label for>
  let labelId = select.getAttribute('aria-labelledby') || '';
  if (!labelId && select.labels && select.labels[0]) {
    select.labels[0].id = select.labels[0].id || `${id}-label`;
    labelId = select.labels[0].id;
  }
  const labelled = labelId ? { 'aria-labelledby': labelId } : {};

  const wrap = el('div', 'rd-select');
  const native = el('span', 'rd-select__native');
  const trigger = el('button', 'rd-select__trigger', {
    type: 'button',
    role: 'combobox',
    'aria-haspopup': 'listbox',
    'aria-expanded': 'false',
    'aria-controls': `${id}-list`,
    ...labelled,
  });
  const value = el('span', 'rd-select__value');
  const list = el('div', 'rd-select__list', { id: `${id}-list`, role: 'listbox', ...labelled });
  list.hidden = true;
  list.tabIndex = -1; // klepnutí do seznamu (iOS) přesune fokus sem, ne „nikam“ – blur tlačítka ho pak nezavře
  trigger.appendChild(value);

  const items = []; // { node, option } v pořadí selectu
  const addOption = (option, parent) => {
    if (option.hidden) {
      return;
    }
    const node = el('div', 'rd-select__option', { id: `${id}-opt-${items.length}`, role: 'option' });
    node.textContent = option.textContent.trim();
    if (option.disabled) {
      node.setAttribute('aria-disabled', 'true');
    }
    parent.appendChild(node);
    items.push({ node, option });
  };
  Array.from(select.children).forEach((child, i) => {
    if (child.tagName === 'OPTGROUP') {
      const group = el('div', 'rd-select__group', { role: 'group', 'aria-labelledby': `${id}-group-${i}` });
      const title = el('div', 'rd-select__group-label', { id: `${id}-group-${i}`, role: 'presentation' });
      title.textContent = child.label;
      group.appendChild(title);
      Array.from(child.children).forEach((option) => addOption(option, group));
      list.appendChild(group);
    } else if (child.tagName === 'OPTION') {
      addOption(child, list);
    }
  });
  if (!items.length) {
    return;
  }

  select.before(wrap);
  native.appendChild(select);
  wrap.append(native, trigger, list);

  // Select sám není k ovládání: z pořadí Tab pryč, fokus (klik na <label>, lišta kroků v order.js,
  // chyba validace) se přehodí na tlačítko.
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  select.addEventListener('focus', () => trigger.focus({ preventScroll: true }));

  let active = -1; // index do items

  const isOpen = () => !list.hidden;

  function setActive(index, scroll = true) {
    items.forEach((item, i) => item.node.classList.toggle('is-active', i === index));
    active = index;
    if (index < 0) {
      trigger.removeAttribute('aria-activedescendant');
      return;
    }
    trigger.setAttribute('aria-activedescendant', items[index].node.id);
    if (scroll) {
      items[index].node.scrollIntoView({ block: 'nearest' });
    }
  }

  // další povolená volba od `from` ve směru `dir` (1 / -1); na konci seznamu zůstane, kde je
  function step(from, dir) {
    for (let i = from + dir; i >= 0 && i < items.length; i += dir) {
      if (!items[i].option.disabled) {
        return i;
      }
    }
    return from;
  }

  // psaní: další volba začínající tím znakem, dokola od aktivní
  function matchChar(char) {
    const needle = char.toLocaleLowerCase('cs');
    for (let n = 1; n <= items.length; n += 1) {
      const i = (active + n + items.length) % items.length;
      if (!items[i].option.disabled && items[i].node.textContent.toLocaleLowerCase('cs').startsWith(needle)) {
        return i;
      }
    }
    return active;
  }

  function sync() {
    const selected = select.options[select.selectedIndex] || null;
    value.textContent = selected ? selected.textContent.trim() : '';
    items.forEach((item) => item.node.setAttribute('aria-selected', item.option === selected ? 'true' : 'false'));
  }

  function onOutside(event) {
    if (!wrap.contains(event.target)) {
      close();
    }
  }

  function open() {
    if (isOpen()) {
      return;
    }
    list.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    const current = items.findIndex((item) => item.option.selected);
    setActive(current >= 0 ? current : step(-1, 1));
    list.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); // u spodního okraje obrazovky dojet na celý seznam
    document.addEventListener('pointerdown', onOutside);
  }

  function close() {
    if (!isOpen()) {
      return;
    }
    list.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    setActive(-1);
    document.removeEventListener('pointerdown', onOutside);
  }

  function choose(index) {
    const item = items[index];
    if (!item || item.option.disabled) {
      return false;
    }
    if (!item.option.selected) {
      item.option.selected = true;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync();
    return true;
  }

  trigger.addEventListener('click', () => {
    trigger.focus(); // Safari tlačítko po kliknutí nefokusuje – bez toho by nešly šipky ani zavření při odchodu
    if (isOpen()) {
      close();
    } else {
      open();
    }
  });

  // na obalu, ne na tlačítku: po klepnutí do seznamu může být fokus na seznamu
  wrap.addEventListener('keydown', (event) => {
    const { key } = event;
    const printable = key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey;

    if (!isOpen()) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'].includes(key) || printable) {
        event.preventDefault();
        open();
        if (key === 'Home') {
          setActive(step(-1, 1));
        } else if (key === 'End') {
          setActive(step(items.length, -1));
        } else if (printable) {
          setActive(matchChar(key));
        }
      }
      return;
    }

    if (key === 'ArrowDown') {
      event.preventDefault();
      setActive(step(active, 1));
    } else if (key === 'ArrowUp' && event.altKey) {
      event.preventDefault();
      choose(active);
      close();
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      setActive(step(active, -1));
    } else if (key === 'Home') {
      event.preventDefault();
      setActive(step(-1, 1));
    } else if (key === 'End') {
      event.preventDefault();
      setActive(step(items.length, -1));
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      choose(active);
      close();
    } else if (key === 'Escape') {
      event.preventDefault();
      close();
    } else if (key === 'Tab') {
      choose(active); // podle vzoru ARIA Tab potvrdí aktivní volbu a fokus jde dál
      close();
    } else if (printable) {
      event.preventDefault();
      setActive(matchChar(key));
    }
  });

  // mezerník by na keyup ještě „kliknul“ na tlačítko a seznam znovu otevřel
  trigger.addEventListener('keyup', (event) => {
    if (event.key === ' ') {
      event.preventDefault();
    }
  });

  // fokus odešel mimo seznam (Tab, klik jinam) → zavřít
  wrap.addEventListener('focusout', (event) => {
    if (!wrap.contains(event.relatedTarget)) {
      close();
    }
  });

  // klik myší do seznamu nechá fokus na tlačítku, aby dál fungovaly šipky
  list.addEventListener('mousedown', (event) => event.preventDefault());

  const indexOf = (target) => {
    const node = target.closest('.rd-select__option');
    return node ? items.findIndex((item) => item.node === node) : -1;
  };

  list.addEventListener('mousemove', (event) => {
    const index = indexOf(event.target);
    if (index >= 0 && index !== active && !items[index].option.disabled) {
      setActive(index, false);
    }
  });

  list.addEventListener('click', (event) => {
    if (choose(indexOf(event.target))) {
      close();
      trigger.focus({ preventScroll: true });
    }
  });

  select.addEventListener('change', sync);
  if (select.form) {
    select.form.addEventListener('reset', () => setTimeout(sync));
  }
  sync();
}
